import AIRPORTS from './airport-database.json'
import {headingDiff} from "./nav-calc.js";

function findAirportByCode(airport_code) {
    const airport = AIRPORTS.find(airport => airport.airport_code === airport_code);
    return airport;
}

export function getAirportDetails(airport_code) {
    const airport = findAirportByCode(airport_code);
    return airport ? {
        airport_code, 
        airport_name: airport.airport_name, 
        elevation: parseInt(airport.elevation),
        operational_hours: airport.operational_hours
    } : null;
}

export function findAirportByName(searchValue) {
    if (!searchValue) {
        return null;
    }
  
    const predictions = AIRPORTS.filter(airport => airport.airport_name.toUpperCase().startsWith(searchValue.toUpperCase()));
    predictions.sort((a, b) => a.airport_name.localeCompare(b.airport_name));

    if (predictions && predictions.length > 0) {
        return predictions[0];
    } else {
        return null;
    }
}

export function getRunways(airport_code) {
    const airport = findAirportByCode(airport_code);
    return airport.runways;
}

export function getActiveRunway(airport_code, wind_direction) {
    const runways = getRunways(airport_code);
    // take the runway with the smallest difference between the wind direction and the runway heading
    runways.sort((a, b) => {
        const aDiff = headingDiff(wind_direction, parseInt(a.runway) * 10);
        const bDiff = headingDiff(wind_direction, parseInt(b.runway) * 10);
        // if the differences are equal, take the runway with the wind on the right side
        if (Math.abs(aDiff) === Math.abs(bDiff)) {
            return aDiff - bDiff;
        }
        return Math.abs(aDiff) - Math.abs(bDiff);
    });
    return runways[0];
}

export function getRunwayDistances(airport_code, runway) {
    const airport = findAirportByCode(airport_code);
    const runwayData = airport.runways.find(r => r.runway === runway);

    const TORA = runwayData.runway_distances.TORA;
    const TODA = runwayData.runway_distances.TODA;
    const ASDA = runwayData.runway_distances.ASDA;
    const LDA = runwayData.runway_distances.LDA;

    return {
        TORA: TORA ? parseInt(TORA) : null,
        TODA: TODA ? parseInt(TODA) : null,
        ASDA: ASDA ? parseInt(ASDA) : null,
        LDA: LDA ? parseInt(LDA) : null
    };
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getWeekdays(startDay = "Mon") {
    const startIndex = WEEKDAYS.indexOf(startDay);
    const result = [];
    for (let i = 0; i < 7; i++) {
        const index = (startIndex + i) % 7;
        result.push(WEEKDAYS[index]);
    }
    return result;
}

function getDayOfWeek(date) {
    return date.toLocaleString('en-US', { weekday: 'short' });  // Returns 'Mon', 'Tue', etc.
}

function parseFromToTime(fromToTime) {
    const [fromTime, toTime] = fromToTime.split('-');
    const [fromHours, fromMinutes] = fromTime.split(':').map(Number);
    const [toHours, toMinutes] = toTime.split(':').map(Number);
    return {
        from: new Date(0, 0, 0, fromHours, fromMinutes),
        to: new Date(0, 0, 0, toHours, toMinutes)
    };
}   

function getOpenTimesForWeekday(airport_code, scheduleType, dayName) {
    const airport = findAirportByCode(airport_code);
    const operational_hours = airport.operational_hours[scheduleType][dayName];
    if (operational_hours === 'Closed') {
        return [];
    }

    const openTimes = Array.isArray(operational_hours) 
        ? operational_hours.map(parseFromToTime)
        : [parseFromToTime(operational_hours)];
    return openTimes;
}

export const SCHEDULE_TYPES = {
    AD: 'AD',
    FUEL: 'fuel',
    CUSTOMS: 'customs'
}

export function isAirportOpen(airport_code, scheduleType, dateTime) {
    const date = new Date(dateTime);
    const dayName = getDayOfWeek(date);
    const time = new Date(0, 0, 0, date.getHours(), date.getMinutes())
    
    const openTimes = getOpenTimesForWeekday(airport_code, scheduleType, dayName);
    for (const openTime of openTimes) {
        if (time >= openTime.from && time <= openTime.to) {
            return true;
        }
    }
    return false;
}

export function getAirportOpenTime(airport_code, scheduleType, afterTime) {
    if (isAirportOpen(airport_code, scheduleType, afterTime)) {
        return afterTime;
    }

    const date = new Date(afterTime);
    const weekdays = getWeekdays(getDayOfWeek(date));
    
    // Check remaining times for current day
    let currentDay = getDayOfWeek(date);
    let openTimes = getOpenTimesForWeekday(airport_code, scheduleType, currentDay);
    
    // Find next opening time today
    for (const openTime of openTimes) {
        const openDateTime = new Date(date);
        openDateTime.setHours(openTime.from.getHours());
        openDateTime.setMinutes(openTime.from.getMinutes());
        
        if (openDateTime > afterTime) {
            return openDateTime;
        }
    }
    
    // Check subsequent days
    for (let i = 1; i < 7; i++) {
        const nextDay = weekdays[i];
        openTimes = getOpenTimesForWeekday(airport_code, scheduleType, nextDay);
        
        if (openTimes.length > 0) {
            // Found next day with opening hours
            const nextOpenTime = new Date(date);
            nextOpenTime.setDate(date.getDate() + i);
            nextOpenTime.setHours(openTimes[0].from.getHours());
            nextOpenTime.setMinutes(openTimes[0].from.getMinutes());
            return nextOpenTime;
        }
    }
    
    return null; // Should never reach here if airport has any opening hours
}

export function isFuelAvailable(airport_code, fuelType) {
    const airport = findAirportByCode(airport_code);
    const fuelTypes = airport.fuel_types;
    return fuelTypes[fuelType] === 'Yes';
}
